// frontend/src/pages/model/ModelPage.jsx
// The Model page (Phase 3 step 13b), at /model: how each dataset's fields are
// classified. Pick a dataset on the left; on the right, its name and
// certification, and a table with every field the profiler found, hidden ones
// included: its label, role, what it starts as when used as a value, the grain
// a date starts with, the charts that use it, and whether the builder shows it.
//
// Anyone signed in can read the model; the server says who can change it
// (can_edit: admins), and the server enforces that. Each change is saved on its
// own, right away, through PATCH /semantic/datasets/{id}/fields/{key}; the row
// shows the server's answer. Hiding a field that saved charts use is refused by
// the server (FIELD_IN_USE) and the page asks before sending it again forced.
// The selected dataset lives in the URL (?dataset=ID), like the builder's.
// 13c-3: below the fields, the Calculations panel (CalculationsSection): the
// dataset's calculated columns and measures, with the formula editor.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BadgeCheck, Database, Info, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { EmptyState } from '@/components/ui/empty-state.jsx';
import { Panel, PanelHint } from '@/components/ui/panel.jsx';
import { SemanticError, getModel, listDatasets, updateModelDataset, updateModelField } from '../../charts/semanticApi';
import DatasetHeader from './DatasetHeader';
import FieldInUseDialog from './FieldInUseDialog';
import FieldTable from './FieldTable';
import CalculationsSection from './CalculationsSection';
import { SHOW_FILTERS, countFields, errorText, filterFields, groupBySource, replaceField, without } from './modelLogic';

const IDLE = { status: 'idle', dataset: null, fields: [], canEdit: false, error: null };

function Notice({ children, onRetry }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <p className="max-w-sm text-sm text-[var(--ui-text-muted)]">{children}</p>
      {onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

function Loading({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-[var(--ui-text-muted)]" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {label}
    </div>
  );
}

export default function ModelPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState({ status: 'loading', datasets: [], error: null });
  const [attempt, setAttempt] = useState(0);
  const [model, setModel] = useState(IDLE);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState({});          // { [field key]: true } while that field is being saved
  const [errors, setErrors] = useState({});      // { [field key]: text } the last failed change
  const [confirm, setConfirm] = useState(null);  // { key, patch, charts, fieldName } waiting for "go ahead anyway"
  const [ds, setDs] = useState({ busy: false, error: '' });
  const [query, setQuery] = useState('');
  const [show, setShow] = useState('all');

  // --- the datasets of the organization ---------------------------------------
  useEffect(() => {
    const ctrl = new AbortController();
    setList((s) => ({ ...s, status: 'loading', error: null }));
    listDatasets({ signal: ctrl.signal })
      .then((datasets) => setList({ status: 'ready', datasets, error: null }))
      .catch((error) => { if (!ctrl.signal.aborted) setList({ status: 'error', datasets: [], error }); });
    return () => ctrl.abort();
  }, [attempt]);

  const groups = useMemo(() => groupBySource(list.datasets), [list.datasets]);
  const selectedId = Number(searchParams.get('dataset')) || null;
  const selected = list.datasets.find((d) => d.id === selectedId) || null;   // an id that is not the caller's own is ignored
  const firstId = groups.length ? groups[0].items[0].id : null;
  useEffect(() => {
    if (list.status === 'ready' && !selected && firstId) {
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('dataset', String(firstId)); return next; }, { replace: true });
    }
  }, [list.status, selected, firstId]);   // eslint-disable-line react-hooks/exhaustive-deps

  // --- the selected dataset's model --------------------------------------------
  const currentId = selected ? selected.id : null;
  const datasetIdRef = useRef(null);
  datasetIdRef.current = currentId;
  const fieldsRef = useRef([]);
  fieldsRef.current = model.fields;

  useEffect(() => {
    setBusy({}); setErrors({}); setConfirm(null); setDs({ busy: false, error: '' }); setQuery(''); setShow('all');
  }, [currentId]);

  useEffect(() => {
    if (!currentId) { setModel(IDLE); return undefined; }
    const ctrl = new AbortController();
    // A reload after a formula changed (13c-3) keeps the table on screen; a new dataset shows the loading state.
    setModel((m) => (reload > 0 && m.status === 'ready' ? m : { ...IDLE, status: 'loading' }));
    getModel(currentId, { signal: ctrl.signal })
      .then((body) => setModel({ status: 'ready', dataset: body.dataset, fields: body.fields || [], canEdit: body.can_edit === true, error: null }))
      .catch((error) => { if (!ctrl.signal.aborted) setModel({ ...IDLE, status: 'error', error }); });
    return () => ctrl.abort();
  }, [currentId, reload]);
  const refreshModel = useCallback(() => setReload((n) => n + 1), []);

  // --- changes -----------------------------------------------------------------
  const changeField = useCallback(async (key, patch) => {
    const datasetId = datasetIdRef.current;
    if (!datasetId) return;
    setBusy((b) => ({ ...b, [key]: true }));
    setErrors((e) => without(e, key));
    try {
      const updated = await updateModelField(datasetId, key, patch);
      if (datasetIdRef.current !== datasetId) return;
      setModel((m) => ({ ...m, fields: replaceField(m.fields, updated) }));
      setConfirm(null);
    } catch (err) {
      if (datasetIdRef.current !== datasetId) return;
      if (err instanceof SemanticError && err.code === 'FIELD_IN_USE') {
        const field = fieldsRef.current.find((f) => f.key === key);
        setConfirm({
          key, patch, charts: err.charts, calculations: err.calculations, fieldName: field ? field.display_name : key,
          count: Math.max(err.charts.length, field ? field.used_in_charts : 0),
        });
      } else {
        setConfirm(null);
        setErrors((e) => ({ ...e, [key]: errorText(err) }));
      }
    } finally {
      if (datasetIdRef.current === datasetId) setBusy((b) => without(b, key));
    }
  }, []);

  const changeDataset = useCallback(async (patch) => {
    const datasetId = datasetIdRef.current;
    if (!datasetId) return;
    setDs({ busy: true, error: '' });
    try {
      const updated = await updateModelDataset(datasetId, patch);
      if (datasetIdRef.current !== datasetId) return;
      setModel((m) => ({ ...m, dataset: { ...m.dataset, ...updated } }));
      setList((l) => ({
        ...l,
        datasets: l.datasets.map((d) => (d.id === datasetId ? { ...d, display_name: updated.display_name, is_certified: updated.is_certified } : d)),
      }));
      setDs({ busy: false, error: '' });
    } catch (err) {
      if (datasetIdRef.current === datasetId) setDs({ busy: false, error: errorText(err) });
    }
  }, []);

  const select = (id) => setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('dataset', String(id)); return next; });

  const shownFields = useMemo(() => filterFields(model.fields, { query, show }), [model.fields, query, show]);
  const counts = useMemo(() => countFields(model.fields), [model.fields]);
  const viewCount = { all: counts.total, visible: counts.total - counts.hidden, hidden: counts.hidden, used: counts.used };
  const confirmBusy = Boolean(confirm && busy[confirm.key]);

  // --- the page ----------------------------------------------------------------
  return (
    <div className="flex flex-col gap-4">
      <Panel className="py-3.5">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2.5">
          <h2 className="text-sm font-semibold tracking-[-0.01em] text-[var(--ui-text)]">Datasets</h2>
          {list.status === 'loading' && (
            <span className="flex items-center gap-2 text-sm text-[var(--ui-text-muted)]" role="status">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading datasets
            </span>
          )}
          {list.status === 'error' && (
            <span className="flex items-center gap-3 text-sm text-[var(--ui-text-muted)]">
              {errorText(list.error)}
              <Button variant="secondary" size="sm" onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
            </span>
          )}
          {list.status === 'ready' && groups.length === 0 && (
            <span className="text-sm text-[var(--ui-text-muted)]">None yet. Datasets appear once a connector has synced.</span>
          )}
          {groups.map((g) => (
            <div key={g.key} className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-[var(--ui-text-muted)]">{g.label}</span>
              {g.items.map((d) => {
                const active = d.id === currentId;
                return (
                  <button
                    key={d.id} type="button" onClick={() => select(d.id)} aria-current={active ? 'true' : undefined}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
                      active ? 'bg-[var(--ui-selected)] text-[var(--ui-selected-text)]' : 'bg-[var(--ui-inset)] text-[var(--ui-text)] hover:bg-[var(--ui-inset-hover)]'}`}
                  >
                    <span className="max-w-[220px] truncate">{d.display_name}</span>
                    {d.is_certified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-emerald-500" role="img" aria-label="Certified" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </Panel>

        <Panel className="min-h-[320px]">
          {list.status === 'ready' && groups.length === 0 && (
            <EmptyState
              icon={Database} title="No datasets yet"
              description="A dataset is made when a connector syncs. Connect a source, and its fields will be listed here to classify."
              action={<Button asChild><Link to="/connectors">Go to Connectors</Link></Button>}
            />
          )}
          {currentId && model.status === 'loading' && <Loading label="Loading fields" />}
          {currentId && model.status === 'error' && <Notice onRetry={() => setReload((n) => n + 1)}>{errorText(model.error)}</Notice>}
          {currentId && model.status === 'ready' && (
            <>
              <DatasetHeader dataset={model.dataset} canEdit={model.canEdit} busy={ds.busy} error={ds.error} onChange={changeDataset} />
              <PanelHint className="flex items-start gap-1.5">
                <Info className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
                <span>
                  {model.canEdit
                    ? 'Set how each field behaves when someone builds a chart. What you set here is where a field starts when it is added to a chart; saved charts keep their own settings. Hiding or ignoring a field takes it out of every chart that uses it.'
                    : 'This is how the dataset is set up for the chart builder. Only admins can change it.'}
                </span>
              </PanelHint>

              <div className="mb-3 mt-4 flex flex-wrap items-center gap-3">
                <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ui-text-muted)]" aria-hidden="true" />
                  <input
                    type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search fields" aria-label="Search fields"
                    className="h-9 w-full rounded-xl bg-[var(--ui-inset)] pl-9 pr-3 text-[13px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                  />
                </div>
                <div role="group" aria-label="Show fields" className="inline-flex rounded-xl bg-[var(--ui-inset)] p-0.5">
                  {SHOW_FILTERS.map(([id, label]) => (
                    <button
                      key={id} type="button" aria-pressed={show === id} onClick={() => setShow(id)}
                      className={`rounded-[10px] px-3 py-1 text-[12.5px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
                        show === id ? 'bg-[var(--ui-selected)] text-[var(--ui-selected-text)]' : 'text-[var(--ui-text-muted)] hover:text-[var(--ui-text)]'}`}
                    >
                      {label} <span className="opacity-70">{viewCount[id]}</span>
                    </button>
                  ))}
                </div>
              </div>

              <FieldTable
                fields={shownFields} canEdit={model.canEdit} busy={busy} errors={errors} onChange={changeField}
                emptyText={model.fields.length === 0 ? 'This dataset has no fields yet.' : 'No fields match.'}
              />
            </>
          )}
        </Panel>

      {currentId && model.status === 'ready' && (
        <CalculationsSection datasetId={currentId} canEdit={model.canEdit} fields={model.fields} onChanged={refreshModel} />
      )}

      <FieldInUseDialog
        request={confirm} busy={confirmBusy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { if (confirm) changeField(confirm.key, { ...confirm.patch, force: true }); }}
      />
    </div>
  );
}
